/**
 * Módulo de roles y permisos — CRUD de roles, asignación de permisos y consulta de matriz
 */

import { createCrudMethods } from '../base/crudMethods';

/**
 * Crea el módulo de roles y permisos (RBAC)
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @returns {Object} Métodos del módulo RBAC
 */
export function createRbacModule({ authenticatedRequest }) {
  return {
    ...createCrudMethods(
      {
        endpoint: '/api/roles',
        entityName: 'rol',
        entityNamePlural: 'roles',
        methods: { getById: false }
      },
      { authenticatedRequest }
    ),

    /**
     * Obtiene los permisos del usuario autenticado
     *
     * @returns {Promise<{success: boolean, data: string[]}>}
     */
    async getMisPermisos() {
      return await authenticatedRequest('/api/auth/me/permisos');
    },

    /**
     * Obtiene los permisos de un rol (todos los permisos con flag asignado)
     *
     * @param {number} rolId - ID del rol
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getRolPermisos(rolId) {
      return await authenticatedRequest(`/api/roles/${rolId}/permisos`);
    },

    /**
     * Actualiza los permisos de un rol (reemplaza todos)
     *
     * @param {number} rolId - ID del rol
     * @param {number[]} permisosIds - Array de IDs de permisos a asignar
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async updateRolPermisos(rolId, permisosIds) {
      return await authenticatedRequest(`/api/roles/${rolId}/permisos`, {
        method: 'PUT',
        body: JSON.stringify({ permisos: permisosIds })
      });
    },

    /**
     * Lista todos los permisos agrupados por módulo
     *
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getPermisos() {
      return await authenticatedRequest('/api/permisos');
    },

    /**
     * Obtiene la matriz completa roles × permisos
     *
     * @returns {Promise<{success: boolean, data: {roles, permisos, asignaciones}}>}
     */
    async getPermisosMatriz() {
      return await authenticatedRequest('/api/permisos/matriz');
    }
  };
}
